import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const LEVELS = ["easy", "moderate", "expert"];
const LIGHTS = ["low", "medium", "bright indirect"];
const SIZES = ["small", "medium", "large"];

export default function ShopPage() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const level = params.get("level") || "";
  const light = params.get("light") || "";
  const size = params.get("size") || "";
  const q = params.get("q") || "";

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (level) query.care_level = level;
    if (light) query.care_light = light;
    if (size) query.size = size;
    if (q) query.q = q;
    api.get("/plants", { params: query }).then(({ data }) => setPlants(data || [])).finally(() => setLoading(false));
  }, [level, light, size, q]);

  const setFilter = (key, value) => {
    const np = new URLSearchParams(params);
    if (value && np.get(key) !== value) np.set(key, value);
    else np.delete(key);
    setParams(np);
  };

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <div className="mb-16">
        <div className="overline text-[var(--gu-muted)] mb-4">The greenhouse</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none">Every plant, one at a time.</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-x-10 gap-y-6 py-6 border-y border-[var(--gu-border)] mb-16">
        <FilterGroup label="Care" options={LEVELS} value={level} onSelect={(v) => setFilter("level", v)} testPrefix="filter-level" />
        <FilterGroup label="Light" options={LIGHTS} value={light} onSelect={(v) => setFilter("light", v)} testPrefix="filter-light" />
        <FilterGroup label="Size" options={SIZES} value={size} onSelect={(v) => setFilter("size", v)} testPrefix="filter-size" />
      </div>

      {loading ? (
        <div className="text-[var(--gu-muted)] font-display text-2xl">Gathering leaves…</div>
      ) : plants.length === 0 ? (
        <div className="text-[var(--gu-muted)] font-display text-2xl py-16">Nothing matches. Try fewer filters.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
          {plants.map((p) => (
            <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`plant-card-${p.slug}`} className="group block">
              <div className="aspect-[3/4] overflow-hidden bg-[var(--gu-surface-2)] relative">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover image-hover" />
                {p.stock <= 3 && (
                  <div className="absolute top-4 left-4 overline bg-[var(--gu-bg)] px-3 py-1">Only {p.stock} left</div>
                )}
              </div>
              <div className="mt-6 flex items-baseline justify-between gap-4">
                <div>
                  <div className="font-display text-2xl leading-tight">{p.name}</div>
                  <div className="text-xs text-[var(--gu-muted)] mt-1 italic">{p.scientific_name}</div>
                  <div className="overline text-[var(--gu-muted)] mt-3">{p.care_level} · {p.size}</div>
                </div>
                <div className="text-base font-medium whitespace-nowrap">${p.price.toFixed(0)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, options, value, onSelect, testPrefix }) {
  return (
    <div className="flex items-center gap-6 flex-wrap">
      <span className="overline text-[var(--gu-muted)]">{label}</span>
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testPrefix}-${o.replace(/\s+/g, '-')}`}
          onClick={() => onSelect(o)}
          className={`overline pb-1 border-b transition-colors ${value === o ? "border-[var(--gu-primary)] text-[var(--gu-primary)]" : "border-transparent text-[var(--gu-muted)] hover:text-[var(--gu-primary)]"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
